"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMarketOpen } from "@/lib/useMarketOpen";
import { msSinceLastClose } from "@/lib/marketHours";
import { CLOSE_ROLL_GRACE_MS, snapshotFreshness } from "@/lib/scanner/snapshotFreshness";
import { isPendingBody, type PendingBody } from "@/lib/http/pending";
import { catchUpDue, pollDue } from "@/lib/http/pollPace";

export type { PendingBody };
export { isPendingBody };

type CacheEntry<T> = {
  data: T;
  fetchedAt: number;
  /** ETag of the cached body, when the route emitted one. Refresh ticks
   *  send it back as If-None-Match; a 304 keeps the cached object (no
   *  re-download, no re-parse, no identity-changing re-render). */
  etag?: string | null;
};

const clientCache = new Map<string, CacheEntry<unknown>>();

/** Invalidate the in-process client cache for a URL so the next mount
 *  (or the next `bumpedDeps` tick) of useFetch fetches fresh. Use this
 *  when an out-of-band action (settings save, manual refresh) makes a
 *  cached payload stale. No-op if the URL isn't cached. */
export function invalidateClientCache(url: string): void {
  clientCache.delete(url);
}

/** Drop every cached GET whose URL is `prefix` or starts with it
 *  (`/api/timing/top-down/ad` also clears `...?universe=all`). Settings
 *  → Breadth toggles one filter that many pane URLs read. */
export function invalidateClientCachePrefix(prefix: string): void {
  for (const url of clientCache.keys()) {
    if (url === prefix || url.startsWith(prefix)) clientCache.delete(url);
  }
}
/**
 * Warm the shared client cache for a URL (fire-and-forget) so a later
 * `useFetch(url)` serves it instantly with no loading flash — and, since the
 * request also hits the server, its 6 h cache is warmed too. No-op when the URL
 * is already cached & fresh. Used to pre-compute the standard year ranges.
 */
export async function prefetch(url: string): Promise<void> {
  if (!url) return;
  const cached = clientCache.get(url);
  if (cached && Date.now() - cached.fetchedAt < FRESH_MS) return;
  try {
    const r = await fetch(url);
    const body = await r.json();
    if (r.ok)
      clientCache.set(url, {
        data: body,
        fetchedAt: Date.now(),
        etag: r.headers.get("etag"),
      });
  } catch {
    /* best-effort warm-up */
  }
}

/**
 * How long a cached response is considered "fresh" — within this window,
 * navigating back to a tab serves the cached payload immediately AND skips
 * the background refetch. This avoids the silent re-render churn that
 * occurs when fresh data arrives and downstream `useEffect([data])` hooks
 * (e.g. chart rebuilds) re-run on a new identity-different object.
 *
 * 5 minutes comfortably covers typical tab-hopping while still letting the
 * data refresh on a longer session.
 */
const FRESH_MS = 5 * 60_000;
/** Hung TCP / a wedged web event loop used to leave `loading` true forever
 *  ("Loading AAPL…", blank Smart Read). Abort and surface an error. */
export const FETCH_TIMEOUT_MS = 15_000;

/**
 * A transient failure must not permanently break a pane.
 *
 * Restarting mpp-web closes the socket for ~4 s while the new process boots.
 * Measured 2026-09-08: one restart turned 7 of 200 in-flight requests into hard
 * 502s, and because this hook threw on the first non-OK response, each one
 * rendered as a "This pane did not load" tile that stayed broken until the next
 * poll — or forever, for a pane with no `refreshMs`. Caddy now retries when it
 * cannot DIAL the backend (`lb_try_duration`, see deploy/Caddyfile), which
 * covers the deploy case; this covers the rest, where the backend answers 502
 * or 503 itself, or the user's own connection blips.
 *
 * GET only — that is all this hook issues — so a retry cannot double-apply
 * anything. Delays are jittered because these panes fetch in lockstep: 15 chart
 * tiles refetched on the same MILLISECOND in the log above, and retrying them
 * in lockstep too would just rebuild the same thundering herd.
 */
const TRANSIENT_STATUS = new Set([502, 503, 504]);

/** GET that retries a busy API. The edge answers 503 at 3s while the worker
 *  keeps computing; the next try often hits the cache. Used by panes that
 *  do not go through `useFetch`. */
export async function fetchRetrying(input: string, init?: RequestInit): Promise<Response> {
  const delays = [1000, 3000, 8000];
  let last!: Response;
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    last = await fetch(input, init);
    if (!TRANSIENT_STATUS.has(last.status) || attempt === delays.length) return last;
    await new Promise((r) => setTimeout(r, delays[attempt]));
  }
  return last;
}

/**
 * GET a route that may answer `{ pending: true }`, following the poll until it
 * produces a body.
 *
 * Waiting out a slow build inside one request is what the edge's 3s header
 * budget forbids: the request 503s, the pane prints an error, and the answer
 * lands in the cache a second later with nobody asking for it. The route hands
 * back `pending` instead, and this keeps asking — so the pane shows "loading"
 * for as long as it is genuinely loading, and an error only when there is one.
 *
 * `abort` lets a caller stop when its symbol changed. Gives up after
 * `timeoutMs` so a permanently pending route surfaces rather than spins.
 */
export async function fetchSettled(
  input: string,
  opts: { init?: RequestInit; timeoutMs?: number; aborted?: () => boolean } = {},
): Promise<{ res: Response; body: unknown }> {
  const deadline = Date.now() + (opts.timeoutMs ?? 60_000);
  for (;;) {
    const res = await fetchRetrying(input, opts.init);
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    if (!res.ok || !isPendingBody(body)) return { res, body };
    if (Date.now() >= deadline || opts.aborted?.()) return { res, body };
    const wait = Math.max(500, Math.min(5000, body.retryInMs ?? 1500));
    await new Promise((r) => setTimeout(r, wait));
    if (opts.aborted?.()) return { res, body };
  }
}

function isRetryableFetchError(message: string): boolean {
  return /busy|snapshot not ready|timed out|HTTP 50[234]|Failed to fetch|NetworkError|network/i.test(
    message,
  );
}
const RETRY_DELAYS_MS = [250, 750, 1750];

async function fetchWithRetry(url: string, init: RequestInit, signal: AbortSignal) {
  for (let attempt = 0; ; attempt++) {
    const last = attempt >= RETRY_DELAYS_MS.length;
    try {
      const r = await fetch(url, init);
      if (last || !TRANSIENT_STATUS.has(r.status)) return r;
    } catch (e) {
      // An abort is the caller giving up (timeout or a newer request). Never
      // retry through it, or a stale URL keeps fetching after the switch.
      if (last || signal.aborted) throw e;
    }
    const base = RETRY_DELAYS_MS[attempt]!;
    await new Promise((res) => setTimeout(res, base + Math.random() * base));
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
  }
}

function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const sync = () => setVisible(document.visibilityState === "visible");
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, []);
  return visible;
}

export type UseFetchOpts = {
  refreshMs?: number;
  /**
   * When to run the refresh interval.
   *   - `"rth"` (default): only while the US session is open AND the tab is
   *     visible. Closed/weekend/hidden = one mount fetch, then nothing —
   *     except for `CLOSE_ROLL_GRACE_MS` after the cash close, so a live
   *     tab still downloads the worker's post-close roll. Hidden across
   *     that window is handled by `workerSnapshot` (below).
   *   - `"always"`: keep the cadence whenever the tab is visible (user
   *     data that can change off-hours — watchlists, open trades, presets).
   */
  refreshWhen?: "rth" | "always";
  /**
   * This URL serves a worker-rewritten snapshot with a `computedAt` stamp.
   * The 5-minute client cache and the RTH poll gate would otherwise keep a
   * 15:59 payload all evening — polling stops at the close, so a refresh
   * was what finally downloaded the roll, and the stale-snapshot chip
   * blamed a worker that had already written. When set:
   *   - a cache entry whose stamp fails `snapshotFreshness` is not "fresh"
   *   - becoming visible off-hours refetches that stale stamp once
   */
  workerSnapshot?: boolean;
  /**
   * The route may answer `{ pending: true }` while a worker or a deferred
   * build finishes. Defaults on: a pending body is not data and is not
   * cached. Opt out only for a caller that renders that body itself.
   */
  followPending?: boolean;
};

function workerComputedAt(data: unknown): number | null {
  if (!data || typeof data !== "object") return null;
  const at = (data as { computedAt?: unknown }).computedAt;
  return typeof at === "number" && Number.isFinite(at) ? at : null;
}

function workerSnapshotStale(data: unknown, now: number = Date.now()): boolean {
  const at = workerComputedAt(data);
  return at != null && snapshotFreshness(at, now).stale;
}

export function useFetch<T>(
  url: string,
  deps: unknown[] = [],
  opts: UseFetchOpts = {},
) {
  const { refreshMs, refreshWhen = "rth", workerSnapshot = false, followPending = true } = opts;
  const open = useMarketOpen();
  const visible = useDocumentVisible();
  // A live tab's last RTH payload is stamped ~15:59. Polling used to stop
  // at the bell, so the client sat on that stamp all evening and the
  // stale-snapshot chip read it as "worker did not run". Keep the cadence
  // through the roll grace — the same window `snapshotFreshness` stays
  // quiet — so the post-close write lands without a refresh.
  const sinceClose = open ? null : msSinceLastClose();
  const inCloseCatchup =
    refreshWhen === "rth" &&
    sinceClose != null &&
    sinceClose < CLOSE_ROLL_GRACE_MS;
  // The poll's timer runs while the session (or "always") wants it, shown or
  // hidden: a tick on a hidden page asks nothing. Until 2026-10-27 it was
  // torn down and re-armed with a new random offset at every visibility
  // change, and a page whose polling went off and on every couple of
  // seconds re-read its payloads at each one (lib/http/pollPace.ts).
  const pollWanted =
    !!refreshMs &&
    refreshMs > 0 &&
    (refreshWhen === "always" || open || inCloseCatchup);
  const shouldPoll = pollWanted && visible;
  const intervalMs = pollWanted ? refreshMs! : 0;
  const getCached = () => (clientCache.get(url) as CacheEntry<T> | undefined)?.data ?? null;
  const [data, setData] = useState<T | null>(() => getCached());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(() => getCached() == null);
  const cancelledRef = useRef(false);
  const mountedAtRef = useRef(0);
  const urlRef = useRef(url);
  const abortRef = useRef<AbortController | null>(null);
  const pendingTimerRef = useRef(0);
  /** When the data this hook holds was asked for: its last request, or the cache entry it was served. A poll waits a period from it. */
  const askedAtRef = useRef<number | null>(null);
  urlRef.current = url;

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      abortRef.current?.abort();
      window.clearTimeout(pendingTimerRef.current);
    };
  }, []);

  const doFetch = useCallback((force: boolean) => {
    if (!url) return;
    abortRef.current?.abort();
    window.clearTimeout(pendingTimerRef.current);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const requested = url;
    const stillCurrent = () =>
      !cancelledRef.current && urlRef.current === requested && abortRef.current === ctrl;
    const cachedRaw = clientCache.get(url) as CacheEntry<T> | undefined;
    // A pending body is "not ready", not a payload. Caching it made the
    // poll a fresh hit, so the pane never asked again.
    const cached = cachedRaw && isPendingBody(cachedRaw.data) ? undefined : cachedRaw;
    const isFresh =
      cached &&
      Date.now() - cached.fetchedAt < FRESH_MS &&
      !(workerSnapshot && workerSnapshotStale(cached.data));

    if (cached) {
      setData(cached.data);
      setLoading(false);
      if (isFresh && !force) {
        setError(null);
        askedAtRef.current = cached.fetchedAt;
        return;
      }
    } else if (!force) {
      setData(null);
      setLoading(true);
    } else {
      // A retry with nothing on screen (the 5s 503 retry, a pending poll).
      // Leaving loading false here painted "No data yet" for the whole wait.
      setLoading(true);
    }

    setError(null);
    askedAtRef.current = Date.now();
    const inm = cached?.etag;
    const timer = window.setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
    let sawPending = false;
    fetchWithRetry(
      url,
      {
        signal: ctrl.signal,
        headers: inm ? { "If-None-Match": inm } : undefined,
      },
      ctrl.signal,
    )
      .then(async (r) => {
        if (r.status === 304 && cached) {
          clientCache.set(url, { ...cached, fetchedAt: Date.now() });
          return null;
        }
        // A proxy error body is HTML, not JSON. Without this the pane reports
        // a JSON parse error and hides the status that would explain it.
        const body = await r.json().catch(() => null);
        if (!r.ok) throw new Error(body?.error ?? `HTTP ${r.status}`);
        // A 200 whose body is the login page or the SPA shell parses as
        // nothing. Treating that as data leaves the pane on "No data yet"
        // with no retry.
        if (body == null) throw new Error(`HTTP ${r.status}`);
        return { body: body as T, etag: r.headers.get("etag") };
      })
      .then((res) => {
        if (!res) return;
        if (followPending && isPendingBody(res.body)) {
          sawPending = true;
          clientCache.delete(requested);
          if (!stillCurrent()) return;
          const wait = Math.max(500, Math.min(5_000, res.body.retryInMs ?? 1500));
          pendingTimerRef.current = window.setTimeout(() => {
            if (stillCurrent()) doFetch(true);
          }, wait);
          return;
        }
        clientCache.set(requested, {
          data: res.body,
          fetchedAt: Date.now(),
          etag: res.etag,
        });
        if (stillCurrent()) setData(res.body);
      })
      .catch((e) => {
        if (!stillCurrent()) return;
        if (ctrl.signal.aborted) {
          setError("Request timed out");
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        clearTimeout(timer);
        if (stillCurrent() && !sawPending) setLoading(false);
      });
  }, [url, workerSnapshot, followPending]);

  useEffect(() => {
    mountedAtRef.current = Date.now();
    if (!url) {
      setLoading(false);
      return;
    }
    doFetch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, doFetch, ...deps]);

  // Panes mount together, so a bare setInterval has them all polling on the
  // same tick — the access log caught 15 chart tiles refetching on the SAME
  // millisecond, which is a self-inflicted burst against a single-threaded
  // server. A random offset inside the first period spreads them out; it costs
  // at most one period of extra staleness on the first refresh only. A tick
  // asks only when the data is a period old (`pollDue`), so nothing that asked
  // in between (the mount, a refetch, a catch-up) is asked again early.
  useEffect(() => {
    if (!url || intervalMs <= 0) return;
    let timer = 0;
    const tick = () => {
      if (document.visibilityState === "hidden") return;
      if (!pollDue(askedAtRef.current, Date.now(), intervalMs)) return;
      doFetch(true);
    };
    const skew = window.setTimeout(
      () => {
        tick();
        timer = window.setInterval(tick, intervalMs);
      },
      Math.random() * intervalMs,
    );
    return () => {
      clearTimeout(skew);
      if (timer) clearInterval(timer);
    };
  }, [url, intervalMs, doFetch]);

  // Off-hours the RTH poll is off, so one 503 during a worker restart
  // used to sit on the pane until the next open. Keep trying while the
  // failure is one a later attempt can clear.
  useEffect(() => {
    if (!url || !visible || !error || !isRetryableFetchError(error)) return;
    const timer = window.setTimeout(() => doFetch(true), 5_000);
    return () => clearTimeout(timer);
  }, [url, visible, error, doFetch]);

  // Catch-up when polling becomes needed (tab visible again, or the open
  // bell). Skip the first 2s so useMarketOpen's false→true hydration flip
  // does not double-fetch every RTH mount. Only when the data is a period
  // old or from before the session (`catchUpDue`): until 2026-10-27 this
  // asked at every flip, and a page shown and hidden every 1.9 s asked for
  // Live Breadth's 806 KB series 11,400 times in a session.
  useEffect(() => {
    if (!shouldPoll || !url) return;
    if (Date.now() - mountedAtRef.current < 2000) return;
    if (!catchUpDue(askedAtRef.current, Date.now(), refreshMs!)) return;
    doFetch(true);
    // `refreshMs` is read as of the render that turned polling on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPoll, url, doFetch]);

  // Hidden across the close (or across the whole weekend) never entered
  // `inCloseCatchup`, so the body in the module cache is still Friday
  // 15:59. A look at the tab used to keep serving it — refresh was the
  // only thing that downloaded the roll. One refetch when the stamp is
  // actually stale; a dead worker stays stale and the chip is then true.
  useEffect(() => {
    if (!workerSnapshot || !visible || !url) return;
    const cached = clientCache.get(url) as CacheEntry<T> | undefined;
    if (!cached || !workerSnapshotStale(cached.data)) return;
    doFetch(true);
  }, [workerSnapshot, visible, url, doFetch]);

  const refetch = useCallback(() => doFetch(true), [doFetch]);

  return { data, error, loading, refetch };
}
